import { unwrap } from "@sagot/api-client";
import { LAUNCH_FRAME_ORIGINS_MAX, PLATFORM_ORIGIN } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { Badge, Button, Icon, ListEditor, toast, type ListEditorLabels, type ListEntryResult } from "@sagot/ui";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useErrorMessage } from "../../../../lib/api-errors";
import { useApi } from "../../../../session/session-provider";
import { useWorkspaceId } from "../../../../session/workspace-context";
import { useFormatters } from "../../shared";
import { CardSkeleton, Gate, PanelError, useCanManage } from "../panels/panel-kit";
import { useCustomDomains } from "../use-settings";
import { parseFrameOrigin, sameOrigins, type FrameOriginProblem } from "./frame-origin-model";
import { frameOriginsKey, useFrameOrigins } from "./use-frame-origins";

const K = "launch.frame";
const PROBLEM_KEYS: Readonly<Record<FrameOriginProblem, string>> = {
  invalid: "invalid",
  http: "invalidHttp",
  wildcard: "invalidWildcard",
  path: "invalidPath",
  address: "invalidAddress",
  credentials: "invalidCredentials",
};

/** Where the customer's page frames the chat: the workspace's live custom domain, else the platform origin. */
function useChatOrigin(): string {
  const domains = useCustomDomains();
  const live = domains.data?.find((domain) => domain.status === "active");
  return live ? `https://${live.hostname}` : PLATFORM_ORIGIN;
}

function CspNote({ origin }: { origin: string }) {
  const { t } = useTranslation();
  return (
    <div role="note" className="flex items-start gap-2.5 rounded-md bg-info-soft p-3 text-sm" data-testid="frame-csp-note">
      <Icon name="info" className="mt-0.5 size-4 shrink-0 text-info" />
      <div className="min-w-0">
        <p>{t(`${K}.cspLead`)}</p>
        <code className="my-1.5 block break-all rounded-sm bg-surface px-2 py-1 font-mono text-xs font-semibold">frame-src {origin}</code>
        <p>{t(`${K}.cspTail`)}</p>
      </div>
    </div>
  );
}

/**
 * Secure launch "Show inside your pages" card (mockup secure-launch pin 3, HCU-002): the exact https sites allowed to
 * frame the workspace's hosted chat. Saved with the Save button; the edge sends the list as `frame-ancestors` within a
 * minute. Default (empty): no site can frame the chat.
 */
export function FrameAllowlistCard() {
  const { t } = useTranslation();
  const api = useApi();
  const wsId = useWorkspaceId();
  const queryClient = useQueryClient();
  const message = useErrorMessage();
  const format = useFormatters();
  const manage = useCanManage("settings.manage");
  const query = useFrameOrigins();
  const chatOrigin = useChatOrigin();
  const [draft, setDraft] = useState<string[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);

  if (query.isPending) return <CardSkeleton rows={3} />;
  if (query.isError) return <PanelError error={query.error} onRetry={() => void query.refetch()} title={t(`${K}.loadError`)} />;

  const saved = query.data.origins;
  const items = draft ?? saved;
  const dirty = draft !== null && !sameOrigins(draft, saved);
  const labels: ListEditorLabels = {
    add: t(`${K}.add`),
    placeholder: t(`${K}.placeholder`),
    empty: t(`${K}.empty`),
    count: (count, max) => t(`${K}.count`, { count, max }),
    remove: (origin) => t(`${K}.remove`, { origin }),
    duplicate: (origin) => t(`${K}.duplicate`, { origin }),
    full: t(`${K}.full`, { max: LAUNCH_FRAME_ORIGINS_MAX }),
    added: (origin) => t(`${K}.added`, { origin }),
    removed: (origin) => t(`${K}.removed`, { origin }),
  };
  const parse = (raw: string): ListEntryResult => {
    const result = parseFrameOrigin(raw);
    return result.ok ? result : { ok: false, error: t(`${K}.${PROBLEM_KEYS[result.problem]}`) };
  };
  const change = (next: string[]) => {
    setDraft(next);
    setFailure(null);
  };
  const save = async () => {
    setBusy(true);
    setFailure(null);
    try {
      const result = await unwrap(api.PUT("/v1/webapp/frame-origins", { body: { origins: items } }));
      queryClient.setQueryData(frameOriginsKey(wsId), result);
      setDraft(null);
      toast.success(t(`${K}.saved`));
    } catch (caught) {
      setFailure(message(caught));
    } finally {
      setBusy(false);
    }
  };
  return (
    <section className="card" aria-labelledby="frame-allowlist-title" data-testid="frame-allowlist-card">
      <div className="card-header flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 id="frame-allowlist-title" className="card-title">
            {t(`${K}.title`)}
          </h2>
          <p className="mt-0.5 text-xs text-fg-muted">{t(`${K}.subtitle`)}</p>
        </div>
        <Badge tone={saved.length > 0 ? "success" : "neutral"} dot data-testid="frame-status">
          {t(saved.length > 0 ? `${K}.statusOn` : `${K}.statusOff`)}
        </Badge>
      </div>
      <div className="space-y-4 px-5 py-4">
        <ListEditor
          label={t(`${K}.listLabel`)}
          hint={t(`${K}.hint`)}
          items={items}
          onChange={change}
          parse={parse}
          max={LAUNCH_FRAME_ORIGINS_MAX}
          labels={labels}
          icon="globe"
          disabled={!manage.allowed}
        />
        {manage.allowed ? null : <p className="text-xs text-fg-muted" data-testid="frame-read-only">{manage.sessionReadOnly ? manage.reason : t(`${K}.readOnly`)}</p>}
        {failure ? (
          <p role="alert" className="text-xs font-semibold text-danger" data-testid="frame-error">
            {failure}
          </p>
        ) : null}
        <CspNote origin={chatOrigin} />
        <p className="text-xs text-fg-muted">{t(`${K}.sentAs`)}</p>
      </div>
      <div className="flex flex-wrap items-center justify-between gap-2 border-t border-line px-5 py-3">
        <p className="text-xs text-fg-muted" data-testid="frame-changed">
          {query.data.updated_at ? t(`${K}.lastChanged`, { when: format.relative(query.data.updated_at) }) : t(`${K}.neverChanged`)}
        </p>
        <div className="flex flex-wrap gap-2">
          <Button variant="secondary" disabled={!dirty || busy} onClick={() => { setDraft(null); setFailure(null); }}>
            {t(`${K}.discard`)}
          </Button>
          <Gate reason={manage.reason}>
            <Button loading={busy} disabled={!manage.allowed || !dirty} onClick={() => void save()}>
              {busy ? t(`${K}.saving`) : t(`${K}.save`)}
            </Button>
          </Gate>
        </div>
      </div>
    </section>
  );
}
